import React from 'react';
import {
	createBrowserRouter,
	createRoutesFromChildren,
	matchRoutes,
	Outlet,
	RouterProvider,
	useLocation,
	useNavigationType,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools'
import * as Sentry from "@sentry/react";

import { AuthProvider } from './restapi/AuthProvider';
import { PageHeader } from './components/common/PageHeader';
import { PageFooter } from './components/common/PageFooter';
import { PageHome } from './components/home/PageHome';
import { PageEvent } from './components/event/PageEvent';
import { PagePreview } from './components/preview/PagePreview';
import { PageTimeline } from './components/preview/PageTimeline';
import { ConfirmContainer } from './components/common/dialogs/Confirm';


const queryClient = new QueryClient();

function App() {
	return (
		<Sentry.ErrorBoundary fallback={({error}) =>
			<div>
				<h2>An error occurred</h2>
				<p>{String(error)}</p>
				<p>Get back to the <a href="/">homepage</a>.</p>
			</div>
		}>
			<AuthProvider forceLogin>
				<QueryClientProvider client={queryClient}>
					<PageHeader/>

					<main>
						<Outlet/>
					</main>

					<PageFooter/>
					<ConfirmContainer/>
					<ReactQueryDevtools/>
				</QueryClientProvider>
			</AuthProvider>
		</Sentry.ErrorBoundary>
	);
}

// Sentry config
Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DNS!,
	environment: import.meta.env.VITE_SENTRY_ENV!,
	integrations: [
		Sentry.reactRouterV6BrowserTracingIntegration({
			useEffect: React.useEffect,
			useLocation,
			useNavigationType,
			createRoutesFromChildren,
			matchRoutes,
		}),
	],
	tracesSampleRate: 0.0,
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(
	createBrowserRouter,
);

// Router config
const router = sentryCreateBrowserRouter([
	{
		element: <App/>,
		children: [
			{path: "/", Component: PageHome},
			{path: "/event/:id", Component: PageEvent},
			{path: "/timeline/:id", Component: PageTimeline},
			{path: "/table-preview/:month", Component: PagePreview},
			{path: "/doc-preview/:month", Component: PagePreview},
			{path: "/:from", Component: PageHome},
		],
	},
]);

export function RoutesWrappedApp() {
	return <RouterProvider router={router}/>;
}
